import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import './SetupGuide.css';

function SetupGuide() {
  const [activeStep, setActiveStep] = useState(0);
  
  const steps = [
    {
      title: "Connect your HubSpot account",
      content: "Log in to your ZipCode AutoFill dashboard and click on 'Connect with HubSpot'. You'll be prompted to authorize the integration with your HubSpot account.",
      icon: (
        <svg role="img" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" fill="#FF7A59"><path d="M18.164 7.93V5.084a2.198 2.198 0 001.267-1.978v-.067A2.2 2.2 0 0017.238.845h-.067a2.2 2.2 0 00-2.193 2.193v.067a2.196 2.196 0 001.252 1.973l.013.006v2.852a6.22 6.22 0 00-2.969 1.31l.012-.01-7.828-6.095A2.497 2.497 0 104.3 4.656l-.012.006 7.697 5.991a6.176 6.176 0 00-1.038 3.446c0 1.343.425 2.588 1.147 3.607l-.013-.02-2.342 2.343a1.968 1.968 0 00-.58-.095h-.002a2.033 2.033 0 102.033 2.033 1.978 1.978 0 00-.1-.595l.005.014 2.317-2.317a6.247 6.247 0 104.782-11.134l-.036-.005zm-.964 9.378a3.206 3.206 0 113.215-3.207v.002a3.206 3.206 0 01-3.207 3.207z"/></svg>
      )
    },
    {
      title: "Configure field mappings",
      content: "After connecting, you'll need to configure which fields should be auto-populated. You can customize field mappings for both Contact and Company records.",
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M12 20h9" />
          <path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z" />
        </svg>
      )
    },
    {
      title: "Start using ZipCode AutoFill",
      content: "That's it! Now, whenever you enter a 5-digit ZIP code in your HubSpot forms, the city, state, county, country, and timezone fields will be automatically populated.",
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <polyline points="9 11 12 14 22 4" />
          <path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11" />
        </svg>
      )
    },
  ];

  const handleStepClick = (index) => {
    setActiveStep(index);
  };

  return (
    <div className="setup-guide-container">
      <div className="setup-guide-header">
        <div className="guide-shapes">
          <div className="guide-shape shape1"></div>
          <div className="guide-shape shape2"></div>
          <div className="guide-shape shape3"></div>
        </div>
        <h1>Setup Guide</h1>
        <p className="guide-intro">
          Setting up ZipCode AutoFill with your HubSpot account is quick and easy.
          Follow these steps to start automatically populating address fields from ZIP codes.
        </p>
      </div>
      
      <div className="setup-steps-container">
        <div className="steps-navigation">
          {steps.map((step, index) => (
            <div 
              key={index} 
              className={`step-nav-item ${activeStep === index ? 'active' : ''}`}
              onClick={() => handleStepClick(index)}
            >
              <div className="step-number">{index + 1}</div>
              <div className="step-title">{step.title}</div>
            </div>
          ))}
        </div>
        
        <div className="step-content-container">
          <div className="step-icon">{steps[activeStep].icon}</div>
          <h2>{steps[activeStep].title}</h2>
          <p>{steps[activeStep].content}</p>
        </div>
      </div>
      
      <div className="additional-info">
        <h2>Additional Information</h2>
        
        <div className="info-cards">
          <div className="info-card">
            <div className="info-card-icon">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10" />
                <line x1="12" y1="16" x2="12" y2="12" />
                <line x1="12" y1="8" x2="12.01" y2="8" />
              </svg>
            </div>
            <h3>Our Service</h3>
            <p>
              ZipCode AutoFill allows you to streamline address entry in HubSpot by automatically populating city, state, 
              county, country, and timezone information from a single ZIP code entry.
            </p>
          </div>
          
          <div className="info-card">
            <div className="info-card-icon">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
                <line x1="8" y1="12" x2="16" y2="12" />
                <line x1="12" y1="8" x2="12" y2="16" />
              </svg>
            </div>
            <h3>Backfilling Existing Records</h3>
            <p>
              For customers with access to HubSpot workflows, you can backfill ZIP code data for existing contacts.
              Watch our tutorial video to learn how to set this up.
            </p>
            <div className="video-container">
              <iframe 
                src="https://www.youtube.com/embed/NbnRMGvPW14" 
                title="ZipCode AutoFill Backfilling Tutorial" 
                frameBorder="0" 
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" 
                allowFullScreen>
              </iframe>
            </div>
          </div>
        </div>
      </div>
      <br />
      <div className="setup-cta">
        <h2>Ready to Get Started?</h2>
        <p>Follow the steps above to connect your HubSpot account and start using ZipCode AutoFill.</p>
        <Link to="/dashboard" className="cta-button">Go to Dashboard</Link>
      </div>
    </div>
  );
}

export default SetupGuide;
